import React, { useCallback, useEffect, useRef, useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import CallSplitIcon from "@mui/icons-material/CallSplit";
import CheckIcon from "@mui/icons-material/Check";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import CloseIcon from "@mui/icons-material/Close";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  Autocomplete,
  Box,
  Checkbox,
  FormControlLabel,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import Parameters from "assets/images/Parameters.png";
import { InvestorAutocomplete } from "components/Autocompletes/InvestorAutocomplete";
import { GroupSelectionValue, InvestorGroupAutocomplete } from "components/Autocompletes/InvestorGroupAutocomplete";
import { NumberInput } from "components/forms";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { currenciesArray, currencySign } from "constants/index";
import dayjs from "dayjs";
import { useActionHook } from "hooks";
import { useDealActions, useMeasureParametersSelector } from "store";
import { useAccountSelector, useDateFormatSelect } from "store/selectors/user-selectors";
import { CurrencyType, IDataExtractionTag, IFileRepo, IUpdateFileData, IUpdateFileTagData } from "store/types";
import toIsoMidday from "utils/to-iso-midday";

import { CellEditPopover } from "./CellEditPopover";

const ALL_INVESTORS_ID = -1;

interface Props {
  parameters: IDataExtractionTag[];
  tagErrors?: Record<number, string[]>;
  hoveredPolygonId: string | null;
  onHoverPolygon: (polygonId: string | null) => void;
  onUpdate: (tagId: number, updates: IUpdateFileTagData) => Promise<void>;
  onDelete: (id: number) => void;
  onEdit: (param: IDataExtractionTag, anchorEl: HTMLElement) => void;
  fileData: IFileRepo | null;
  readOnly?: boolean;
  dealInvestors: { id: number; name: string; is_splittable?: boolean }[];
  investorsLoading?: boolean;
  onFileUpdated?: (updatedFile: IFileRepo) => void;
  metadataErrors?: { effectiveDate?: string; currency?: string; investor?: string };
  focusField?: "effectiveDate" | "currency" | "investor" | null;
}

interface EditState {
  anchorEl: HTMLElement;
  paramId: number;
  field: "parameter" | "value";
}

const inputSx = {
  "& .MuiOutlinedInput-root": {
    maxHeight: 32,
    borderRadius: "8px",
    "& fieldset": { borderColor: "#7b92a9" }
  }
};

export const DataExtractionParametersTable: React.FC<Props> = ({
  parameters,
  tagErrors = {},
  hoveredPolygonId,
  onHoverPolygon,
  onUpdate,
  onDelete,
  onEdit,
  fileData,
  readOnly,
  dealInvestors,
  investorsLoading,
  onFileUpdated,
  metadataErrors,
  focusField
}) => {
  const measureParameters = useMeasureParametersSelector();
  const measureOptions = measureParameters.map((m) => ({ id: m.id, name: m.name }));
  const dateFormat = useDateFormatSelect();
  const { allow_split_by_group } = useAccountSelector() || {};

  const { updateFileRepoAction } = useDealActions();
  const { action: patchFile } = useActionHook(updateFileRepoAction, {
    onSuccess: (data) => {
      onFileUpdated?.(data);
    }
  });

  const [effectiveDate, setEffectiveDate] = useState(fileData?.effective_date || "");
  const [currency, setCurrency] = useState(fileData?.currency || "");
  const [investorId, setInvestorId] = useState<number | null>(
    fileData?.all_investors ? ALL_INVESTORS_ID : (fileData?.investor?.id ?? null)
  );
  const [splitByCommitment, setSplitByCommitment] = useState(fileData?.split_by_commitment ?? false);
  const [splitByEntireCommitment, setSplitByEntireCommitment] = useState(fileData?.entire_commitment ?? false);
  const [selectedInvestorIds, setSelectedInvestorIds] = useState<number[]>(fileData?.investor_ids ?? []);

  useEffect(() => {
    if (!fileData) return;
    setEffectiveDate(fileData.effective_date || "");
    setCurrency(fileData.currency || "");
    setInvestorId(fileData.all_investors ? ALL_INVESTORS_ID : (fileData.investor?.id ?? null));
    setSplitByCommitment(fileData.split_by_commitment ?? false);
    setSplitByEntireCommitment(fileData.entire_commitment ?? false);
    setSelectedInvestorIds(fileData.investor_ids ?? []);
    // Re-sync only when switching files; subsequent updates flow through optimistic local state +
    // patchCurrentFile, so re-running this on every fileData mutation can revert the user's action
    // (e.g. the All Investors chip being immediately re-asserted after the X is clicked).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileData?.id]);

  const isDealInvestorSelected =
    investorId !== null && investorId !== ALL_INVESTORS_ID && dealInvestors.some((d) => d.id === investorId);

  const showSplitCheckbox =
    isDealInvestorSelected && dealInvestors.find((d) => d.id === investorId)?.is_splittable !== false;

  const patchCurrentFile = useCallback(
    (updates: Partial<IUpdateFileData>) => {
      if (!fileData) return;
      patchFile({ fileId: fileData.id, ...updates });
    },
    [fileData, patchFile]
  );

  const handleEffectiveDateChange = useCallback(
    (value: string) => {
      setEffectiveDate(value);
      patchCurrentFile({ effective_date: value || null });
    },
    [patchCurrentFile]
  );

  const handleCurrencyChange = useCallback(
    (value: string) => {
      setCurrency(value);
      patchCurrentFile({ currency: (value as IUpdateFileData["currency"]) || null });
    },
    [patchCurrentFile]
  );

  const handleInvestorChange = useCallback(
    (investor: { id: number; name: string } | null, newSplitByCommitment?: boolean) => {
      const isAll = investor?.id === ALL_INVESTORS_ID;
      setInvestorId(investor?.id ?? null);

      const patch: Partial<IUpdateFileData> = {
        investor_id: isAll ? null : (investor?.id ?? null),
        all_investors: isAll,
        split_by_commitment: isAll ? true : (newSplitByCommitment ?? false)
      };

      if (newSplitByCommitment !== undefined) {
        patch.split_by_commitment = newSplitByCommitment;
      }

      patchCurrentFile(patch);
    },
    [patchCurrentFile]
  );

  const handleGroupsChange = useCallback(
    (value: GroupSelectionValue) => {
      setSelectedInvestorIds(value.investorsIds);

      if (value.groupsName.length) {
        setInvestorId(null);
        setSplitByCommitment(true);
        patchCurrentFile({
          investor_id: null,
          all_investors: false,
          investor_ids: value.investorsIds,
          split_by_commitment: true,
          entire_commitment: splitByEntireCommitment,
          group_commitment: !splitByEntireCommitment
        });
      } else if (value.investorId !== null) {
        const isAll = value.investorId === ALL_INVESTORS_ID;
        setInvestorId(value.investorId);
        setSplitByCommitment(false);
        setSplitByEntireCommitment(false);
        setSelectedInvestorIds([]);

        patchCurrentFile({
          investor_id: isAll ? null : value.investorId,
          investor_ids: [],
          all_investors: isAll,
          group_commitment: false,
          entire_commitment: false,
          split_by_commitment: isAll
        });
      } else {
        setInvestorId(null);
        setSplitByCommitment(false);
        setSplitByEntireCommitment(false);
        setSelectedInvestorIds([]);
        patchCurrentFile({
          all_investors: false,
          investor_id: null,
          investor_ids: [],
          split_by_commitment: false,
          group_commitment: false,
          entire_commitment: false
        });
      }
    },
    [patchCurrentFile, dealInvestors, splitByEntireCommitment]
  );

  const effectiveDateRef = useRef<HTMLInputElement>(null);
  const currencyRef = useRef<HTMLInputElement>(null);
  const investorContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!focusField) return;
    if (focusField === "effectiveDate") effectiveDateRef.current?.focus();
    else if (focusField === "currency") currencyRef.current?.focus();
    else if (focusField === "investor") {
      const input = investorContainerRef.current?.querySelector("input");
      input?.focus();
    }
  }, [focusField]);

  const requiresInvestor = parameters.some((p) => p.measure && !p.measure.is_global_value);

  const [editState, setEditState] = useState<EditState | null>(null);
  const [valueInput, setValueInput] = useState<number | null>(null);
  const [parameterInput, setParameterInput] = useState<{ id: number; name: string } | null>(null);

  const handleCellClick = (e: React.MouseEvent<HTMLElement>, paramId: number, field: "parameter" | "value") => {
    const param = parameters.find((p) => p.id === paramId);
    if (param) {
      if (field === "value") setValueInput(param.value);
      if (field === "parameter")
        setParameterInput(param.measure ? { id: param.measure.id, name: param.measure.name } : null);
    }
    setEditState({ anchorEl: e.currentTarget, paramId, field });
  };

  const handleClose = () => setEditState(null);

  const handleParameterApply = async () => {
    if (editState && parameterInput) {
      await onUpdate(editState.paramId, { measure_id: parameterInput.id });
    }
    handleClose();
  };

  const handleValueApply = async () => {
    if (editState && valueInput != null) {
      await onUpdate(editState.paramId, { value: valueInput });
    }
    handleClose();
  };

  const emptyState = !parameters.length && (
    <Stack alignItems="center" justifyContent="center" gap="10px" sx={{ flex: 1, mt: "16px" }}>
      <Box sx={{ height: "145px", width: "315px", overflow: "hidden" }}>
        <Box
          component="img"
          src={Parameters}
          alt=""
          sx={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "top" }}
        />
      </Box>
      <Stack alignItems="center" gap="10px" sx={{ textAlign: "center" }}>
        <Typography sx={{ fontSize: "18px", fontWeight: 500, color: "#071f35" }}>Parameter&apos;s table</Typography>
        <Stack gap="8px" sx={{ width: "100%" }}>
          <Stack direction="row" alignItems="center" gap="30px">
            <Stack direction="row" sx={{ width: 99, flexShrink: 0 }}>
              <Box sx={{ width: 24, height: 24, bgcolor: "rgba(255, 217, 0, 0.2)" }} />
              <Box
                sx={{
                  width: 54,
                  height: 24,
                  bgcolor: "rgba(255, 217, 0, 0.2)",
                  display: "flex",
                  alignItems: "center",
                  pl: "8px"
                }}
              >
                <Typography sx={{ fontSize: "16px", lineHeight: "24px", color: "#0a3142" }}>$100</Typography>
              </Box>
            </Stack>
            <Typography
              component="li"
              sx={{ fontSize: "15px", lineHeight: "20px", color: "#0a3142", listStyleType: "disc", textAlign: "left" }}
            >
              Click a yellow field to tag data.
            </Typography>
          </Stack>

          <Stack direction="row" alignItems="center" gap="30px">
            <Stack direction="row" sx={{ width: 99, flexShrink: 0 }}>
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  bgcolor: "#188100",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}
              >
                <Typography sx={{ fontSize: "14px", fontWeight: 700, lineHeight: "16px", color: "white" }}>
                  3
                </Typography>
              </Box>
              <Box
                sx={{
                  width: 54,
                  height: 24,
                  bgcolor: "rgba(24, 129, 0, 0.2)",
                  display: "flex",
                  alignItems: "center",
                  pl: "8px"
                }}
              >
                <Typography sx={{ fontSize: "16px", lineHeight: "24px", color: "#0a3142" }}>$100</Typography>
              </Box>
            </Stack>
            <Typography
              component="li"
              sx={{ fontSize: "15px", lineHeight: "20px", color: "#0a3142", listStyleType: "disc", textAlign: "left" }}
            >
              Green fields are suggestions from AI or other users.
            </Typography>
          </Stack>

          <Stack direction="row" alignItems="center" gap="30px">
            <Stack direction="row" sx={{ width: 99, flexShrink: 0 }}>
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  bgcolor: "#188100",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}
              >
                <Typography sx={{ fontSize: "14px", fontWeight: 700, lineHeight: "16px", color: "white" }}>
                  3
                </Typography>
              </Box>
              <Box
                sx={{
                  width: 54,
                  height: 24,
                  bgcolor: "rgba(24, 129, 0, 0.2)",
                  display: "flex",
                  alignItems: "center",
                  pl: "8px"
                }}
              >
                <Typography sx={{ fontSize: "16px", lineHeight: "24px", color: "#0a3142" }}>$100</Typography>
              </Box>
              <CheckIcon sx={{ fontSize: 20, color: "#07a27d", ml: "2px" }} />
            </Stack>
            <Typography
              component="li"
              sx={{ fontSize: "15px", lineHeight: "20px", color: "#0a3142", listStyleType: "disc", textAlign: "left" }}
            >
              A ✓ means the item is already saved to the deal ledger.
            </Typography>
          </Stack>
        </Stack>
        <Typography sx={{ fontSize: "15px", fontWeight: 500, lineHeight: "20px", color: "#0a3142", width: "100%" }}>
          Select a yellow value to map it to the right parameter. Review suggestions before saving.
        </Typography>
      </Stack>
    </Stack>
  );

  return (
    <>
      {!readOnly && (
        <Stack gap="8px" sx={{ mb: "8px" }}>
          <Stack direction="row" gap="8px">
            <DesktopDatePicker
              label="Effective Date"
              format={dateFormat}
              views={["year", "month", "day"]}
              value={effectiveDate ? dayjs(effectiveDate) : null}
              onChange={(value) => handleEffectiveDateChange(value ? toIsoMidday(value.toISOString()) : "")}
              inputRef={effectiveDateRef}
              slotProps={{
                textField: {
                  size: "small",
                  fullWidth: true,
                  error: !!metadataErrors?.effectiveDate,
                  helperText: metadataErrors?.effectiveDate,
                  sx: { flex: 1, ...inputSx }
                }
              }}
            />
            <Autocomplete
              options={currenciesArray}
              value={currency || null}
              onChange={(_, value) => handleCurrencyChange(value || "")}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Currency"
                  size="small"
                  inputRef={currencyRef}
                  error={!!metadataErrors?.currency}
                  helperText={metadataErrors?.currency}
                />
              )}
              renderOption={(props, option) => (
                <li {...props} key={option} style={{ fontSize: "16px" }}>
                  {option}
                </li>
              )}
              slotProps={{
                listbox: {
                  sx: {
                    "& .MuiAutocomplete-option": {
                      fontSize: "16px",
                      "&:hover, &.Mui-focused": { bgcolor: "#edf1f9" },
                      "&[aria-selected='true']": { bgcolor: "#edf1f9", "&.Mui-focused": { bgcolor: "#edf1f9" } }
                    }
                  }
                }
              }}
              sx={{ flex: 1, ...inputSx }}
            />
          </Stack>
          <Box ref={investorContainerRef}>
            {allow_split_by_group ? (
              <InvestorGroupAutocomplete
                loading={investorsLoading}
                dealInvestors={dealInvestors}
                preselectedInvestorId={investorId}
                allInvestorsId={ALL_INVESTORS_ID}
                onChange={(inv) => {
                  setSplitByCommitment(false);
                  setInvestorId(inv?.id ?? null);
                }}
                onGroupsChange={handleGroupsChange}
                preselectedInvestorIds={selectedInvestorIds}
                label="Attributed to"
                error={!!metadataErrors?.investor}
                helperText={metadataErrors?.investor}
                sx={inputSx}
              />
            ) : (
              <InvestorAutocomplete
                loading={investorsLoading}
                dealInvestors={dealInvestors}
                preselectedInvestorId={investorId}
                allInvestorsId={ALL_INVESTORS_ID}
                onChange={(inv) => {
                  setSplitByCommitment(false);
                  if (!inv) {
                    handleInvestorChange(null, false);
                    return;
                  }
                  const fullInvestor = dealInvestors.find((d) => d.id === inv.id);
                  const newSplit = fullInvestor?.is_splittable === false ? false : undefined;
                  handleInvestorChange(inv, newSplit);
                }}
                label="Attributed to"
                error={!!metadataErrors?.investor}
                helperText={metadataErrors?.investor}
                sx={inputSx}
              />
            )}
          </Box>
          {investorId === ALL_INVESTORS_ID && !selectedInvestorIds.length && (
            <Typography sx={{ fontSize: "12px", color: "#496682", mt: "4px" }}>
              Values will be split across investors by commitment.
            </Typography>
          )}
          {showSplitCheckbox && !selectedInvestorIds.length && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={splitByCommitment}
                  onChange={(_, checked) => {
                    setSplitByCommitment(checked);
                    patchCurrentFile({ split_by_commitment: checked });
                  }}
                  size="small"
                />
              }
              label="Derive total from this investor's commitment"
              sx={{ "& .MuiFormControlLabel-label": { fontSize: "14px", color: "#071f35" } }}
            />
          )}
          {Boolean(selectedInvestorIds.length) && (
            <MaterialTooltip
              title="Uses this group's total commitment as a share of the deal total to derive values for all non-group investors."
              isShow
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={splitByEntireCommitment}
                    onChange={(_, checked) => {
                      setSplitByEntireCommitment(checked);
                      patchCurrentFile({
                        split_by_commitment: true,
                        group_commitment: !checked,
                        entire_commitment: checked,
                        investor_ids: selectedInvestorIds
                      });
                    }}
                    size="small"
                  />
                }
                label="Derive values for remaining investors."
                sx={{ "& .MuiFormControlLabel-label": { fontSize: "14px", color: "#071f35" } }}
              />
            </MaterialTooltip>
          )}
          {(!effectiveDate || !currency || (requiresInvestor && !investorId)) && (
            <Stack direction="row" alignItems="flex-start" gap="8px" sx={{ py: "4px" }}>
              <InfoOutlinedIcon sx={{ fontSize: 24, color: "#ebaf01", flexShrink: 0, mt: "1px" }} />
              <Stack>
                <Typography sx={{ fontSize: "12px", fontWeight: 500, color: "#496682", lineHeight: "16px" }}>
                  Set document details to save tagging.
                </Typography>
                <Typography sx={{ fontSize: "12px", fontWeight: 400, color: "#496682", lineHeight: "16px" }}>
                  {requiresInvestor
                    ? "Fill in Effective Date, Currency, and Attributed to above."
                    : "Fill in Effective Date and Currency above."}
                </Typography>
              </Stack>
            </Stack>
          )}
        </Stack>
      )}

      {parameters.length > 0 ? (
        <TableContainer
          sx={{
            border: "1px solid #dbe8f5",
            borderRadius: "8px",
            overflow: "auto",
            maxHeight: "calc(100vh - 300px)"
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "#dbe8f5" }}>
                <TableCell sx={{ width: 96, borderBottom: "1px solid #dbe8f5", py: "4px", px: "16px" }} />
                <TableCell
                  sx={{
                    borderBottom: "1px solid #dbe8f5",
                    py: "4px",
                    px: "16px",
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "#071f35"
                  }}
                >
                  Measure
                </TableCell>
                <TableCell
                  sx={{
                    borderBottom: "1px solid #dbe8f5",
                    py: "4px",
                    px: "16px",
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "#071f35"
                  }}
                >
                  Amount
                </TableCell>
                <TableCell
                  sx={{
                    borderBottom: "1px solid #dbe8f5",
                    py: "4px",
                    px: "16px",
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "#071f35"
                  }}
                >
                  AI Classification
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {parameters.map((param, index) => {
                const rowErrors = tagErrors[param.id];
                const hasError = !!rowErrors?.length;
                const isRowHovered = hoveredPolygonId === param.polygon_id;
                return (
                  <TableRow
                    key={param.id}
                    onMouseEnter={() => onHoverPolygon(param.polygon_id)}
                    onMouseLeave={() => onHoverPolygon(null)}
                    sx={{
                      bgcolor: hasError ? "#fef2f2" : isRowHovered ? "#ecf1f8" : "#f6f8fc",
                      position: "relative",
                      "&:hover": { bgcolor: hasError ? "#fef2f2" : "#ecf1f8" },
                      "&:hover .row-actions": { opacity: 1, pointerEvents: "auto" },
                      ...(isRowHovered && { "& .row-actions": { opacity: 1, pointerEvents: "auto" } })
                    }}
                  >
                    <TableCell sx={{ borderBottom: "1px solid #dbe8f5", py: "8px", px: 0 }}>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Box sx={{ width: 32, display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <Typography sx={{ fontSize: "14px", fontWeight: 700, color: "#071f35" }}>
                            {index + 1}
                          </Typography>
                        </Box>
                        <Box sx={{ width: 25, display: "flex", alignItems: "center", justifyContent: "center" }}>
                          {hasError ? (
                            <MaterialTooltip isShow title={rowErrors.join(" ")}>
                              <ErrorOutlineIcon sx={{ fontSize: 20, color: "#d32f2f" }} />
                            </MaterialTooltip>
                          ) : param.is_tagged ? (
                            <CheckOutlinedIcon sx={{ fontSize: 20, color: "#07a27d" }} />
                          ) : (
                            param.is_ai && <AutoAwesomeOutlinedIcon sx={{ fontSize: 20, color: "#c09560" }} />
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell
                      onClick={(e) => handleCellClick(e, param.id, "parameter")}
                      sx={{ borderBottom: "1px solid #dbe8f5", py: "8px", px: "16px", cursor: "pointer" }}
                    >
                      {param.measure?.name ? (
                        <MaterialTooltip
                          isShow
                          title={param.measure.name}
                          disableHoverListener={param.measure.name.length <= 18}
                        >
                          <Typography noWrap sx={{ fontSize: "14px", color: "#26a6ae", maxWidth: "120px" }}>
                            {param.measure.name}
                          </Typography>
                        </MaterialTooltip>
                      ) : (
                        <Box sx={{ position: "relative", width: "24px", height: "24px" }}>
                          <Box
                            sx={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              display: "inline-flex",
                              alignItems: "center",
                              padding: "4px",
                              borderRadius: "32px",
                              cursor: "pointer",
                              bgcolor: "#f5f6f7",
                              zIndex: 1,
                              "& .add-value-label": {
                                maxWidth: 0,
                                opacity: 0,
                                overflow: "hidden",
                                whiteSpace: "nowrap",
                                fontSize: "10px",
                                color: "#071f35",
                                lineHeight: 1,
                                transition: "max-width 0.2s ease, opacity 0.2s ease"
                              },
                              "&:hover": {
                                padding: "4px 8px",
                                bgcolor: "rgba(7, 31, 53, 0.04)",
                                "& .add-value-label": { maxWidth: "80px", opacity: 1 },
                                "& .add-value-icon": { color: "#071f35" }
                              }
                            }}
                          >
                            <AddIcon
                              className="add-value-icon"
                              sx={{ fontSize: 16, color: "#8e99a3", flexShrink: 0 }}
                            />
                            <Box component="span" className="add-value-label">
                              Add Value
                            </Box>
                          </Box>
                        </Box>
                      )}
                    </TableCell>
                    <TableCell
                      onClick={(e) => handleCellClick(e, param.id, "value")}
                      sx={{ borderBottom: "1px solid #dbe8f5", py: "8px", px: "16px", cursor: "pointer" }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: "2px" }}>
                        <Typography noWrap sx={{ fontSize: "14px", color: "#26a6ae" }}>
                          {Intl.NumberFormat("en", {
                            ...(param.currency
                              ? { style: "currency", currency: param.currency }
                              : { style: "decimal" }),
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 2
                          }).format(param.value)}
                        </Typography>
                        {(() => {
                          const changes: string[] = [];
                          if (param.is_manually_changed.value) changes.push("Value");
                          if (param.is_manually_changed.currency) changes.push("Currency");
                          if (param.is_manually_changed.effective_date) changes.push("Effective Date");
                          if (param.is_manually_changed.investor_id) changes.push("Investor");
                          if (!changes.length) return;
                          return (
                            <MaterialTooltip
                              isShow
                              title={`${changes.join(", ")} ${changes.length > 1 ? "differ" : "differs"} from the original PDF settings`}
                            >
                              <ErrorOutlineIcon
                                sx={{ fontSize: 16, color: "#EBAF01", cursor: "pointer", flexShrink: 0 }}
                              />
                            </MaterialTooltip>
                          );
                        })()}
                        {!!param.split_details.length && (
                          <MaterialTooltip
                            isShow
                            title={
                              <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                                {param.split_details.map((d) => (
                                  <Typography key={d.investor_id} sx={{ fontSize: "16px" }}>
                                    {d.investor_name}:{" "}
                                    {Intl.NumberFormat("en", {
                                      style: "currency",
                                      currency: param.currency || "USD",
                                      minimumFractionDigits: 0,
                                      maximumFractionDigits: 2
                                    }).format(d.value)}
                                  </Typography>
                                ))}
                              </Box>
                            }
                          >
                            <CallSplitIcon sx={{ fontSize: 16, color: "#496682", cursor: "pointer", flexShrink: 0 }} />
                          </MaterialTooltip>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ borderBottom: "1px solid #dbe8f5", py: "8px", px: "16px" }}>
                      <Typography noWrap sx={{ fontSize: "14px", color: "#071f35" }}>
                        {param.classification}
                      </Typography>
                    </TableCell>
                    <Box
                      component="td"
                      className="row-actions"
                      sx={{
                        position: "sticky",
                        right: 0,
                        zIndex: 2,
                        padding: 0,
                        border: "none",
                        width: 0,
                        opacity: 0,
                        pointerEvents: "none",
                        transition: "opacity 0.15s"
                      }}
                    >
                      <Box
                        sx={{
                          position: "absolute",
                          right: 0,
                          top: 0,
                          height: "100%",
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                          px: "12px",
                          bgcolor: "#ecf1f8",
                          borderLeft: "1px solid #dbe8f5"
                        }}
                      >
                        <IconButton
                          onClick={(e) => onEdit(param, e.currentTarget)}
                          sx={{ padding: "4px", bgcolor: "rgba(255,255,255,0.96)" }}
                        >
                          <EditOutlinedIcon sx={{ fontSize: 16, color: "#496682" }} />
                        </IconButton>
                        <IconButton
                          onClick={() => onDelete(param.id)}
                          sx={{ padding: "4px", bgcolor: "rgba(255,255,255,0.96)" }}
                        >
                          <CloseIcon sx={{ fontSize: 16, color: "#d32f2f" }} />
                        </IconButton>
                      </Box>
                    </Box>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        emptyState
      )}

      <CellEditPopover
        open={editState?.field === "parameter"}
        anchorEl={editState?.anchorEl}
        onClose={handleClose}
        onApply={handleParameterApply}
      >
        <Autocomplete
          options={measureOptions}
          value={parameterInput}
          onChange={(_, value) => setParameterInput(value)}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderInput={(params) => <TextField {...params} label="Parameter" />}
          renderOption={(props, option) => (
            <li {...props} key={option.id} style={{ fontSize: "14px" }}>
              {option.name}
            </li>
          )}
          slotProps={{
            listbox: {
              sx: {
                "& .MuiAutocomplete-option": {
                  fontSize: "14px",
                  "&:hover, &.Mui-focused": { bgcolor: "#edf1f9" },
                  "&[aria-selected='true']": { bgcolor: "#dbe8f5", "&.Mui-focused": { bgcolor: "#dbe8f5" } }
                }
              }
            }
          }}
          sx={{ width: "100%" }}
        />
      </CellEditPopover>

      <CellEditPopover
        open={editState?.field === "value"}
        anchorEl={editState?.anchorEl}
        onClose={handleClose}
        onApply={handleValueApply}
      >
        <NumberInput
          size="small"
          placeholder="Enter value"
          label="Value"
          fullWidth
          variant="outlined"
          numberInputProps={{
            ...(currency && { prefix: currencySign[currency as CurrencyType] })
          }}
          value={valueInput}
          onChange={(e) => setValueInput(e.target.value ? Number(e.target.value) : null)}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleValueApply();
          }}
          autoFocus
        />
      </CellEditPopover>
    </>
  );
};
